/**
 * Guide Availability Page
 *
 * Page for guides to view and manage their availability.
 * Shows merged calendar (individual dates + recurring blocks) and stats header.
 *
 * SCRUM-821 AC-01, AC-02, AC-03, AC-19
 * Chunk A: Page shell + stats header + data hook.
 * Chunk B: Calendar grid with color coding and click-to-select (AC-04–AC-10).
 * SCRUM-1134: Remove stat header, add month navigation to page header.
 * SCRUM-1135: Convert click-to-toggle → click-to-select (A-02).
 * SCRUM-1136: Remove repeating blocks panel.
 *
 * @component
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import GuideAvailabilityCalendar from '../../components/calendar/GuideAvailabilityCalendar';
import DateDetailPanel from '../../components/calendar/DateDetailPanel';
import BlockDateModal from '../../components/calendar/BlockDateModal';
import CalendarSyncSettings from '../../components/calendar/CalendarSyncSettings';
import RepeatingBlocksList from '../../components/calendar/RepeatingBlocksList';
import UndoToast from '../../components/calendar/UndoToast';
import useGuideAvailability from '../../components/calendar/hooks/useGuideAvailability';
import useUndoToast from '../../hooks/calendar/useUndoToast';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';

export default function GuideAvailabilityPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Selected date for the detail panel (A-03)
  // Selected date for the detail panel (A-03) — set by GuideAvailabilityCalendar via onDateSelect
  const [selectedDate, setSelectedDate] = useState(null);

  // B-03: BlockDateModal state
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);

  // B-NEW-06: Edit mode state (SCRUM-1153)
  const [editingBlockGroup, setEditingBlockGroup] = useState(null);

  // B-09: RepeatingBlocksList state (SCRUM-1154)
  const [showRepeatingBlocks, setShowRepeatingBlocks] = useState(false);

  // Current month for calendar view
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const guideId = user?.id || null;

  const {
    calendarData,
    loading,
    error,
    refresh,
    toggleDate,
    createBlock,
  } = useGuideAvailability({ guideId, currentMonth });

  // A-08: error toast state (shown when API fails on commit or undo path)
  const [errorToast, setErrorToast] = useState(null);

  const { toastState, show: showUndoToast, undo: undoToast, dismiss: dismissToast } = useUndoToast();

  /**
   * A-08: Called after BlockDateModal successfully blocks a date.
   * Shows "Date blocked" undo toast. On commit (5s): no further action needed
   * (toggleDate already applied optimistic update). On undo: re-open the date.
   */
  const handleBlockSuccess = useCallback((dateStr) => {
    showUndoToast({
      message: 'Date blocked',
      onCommit: async () => {
        // Optimistic update already applied — nothing to do on commit
      },
      onUndo: async () => {
        await toggleDate(dateStr, true);
      },
      onCommitError: () => {
        setErrorToast("Couldn't update date — changes reverted");
        // Revert optimistic update
        toggleDate(dateStr, true);
      },
      onUndoError: () => {
        setErrorToast("Couldn't undo — please try again manually");
      },
    });
  }, [showUndoToast, toggleDate]);

  /**
   * A-08: Called after DateDetailPanel successfully re-opens a date.
   * Shows "Date unblocked" undo toast. On undo: re-block the date.
   */
  const handleReopenSuccess = useCallback((dateStr) => {
    showUndoToast({
      message: 'Date unblocked',
      onCommit: async () => {
        // Optimistic update already applied — nothing to do on commit
      },
      onUndo: async () => {
        await toggleDate(dateStr, false);
      },
      onCommitError: () => {
        setErrorToast("Couldn't update date — changes reverted");
        // Revert optimistic update
        toggleDate(dateStr, false);
      },
      onUndoError: () => {
        setErrorToast("Couldn't undo — please try again manually");
      },
    });
  }, [showUndoToast, toggleDate]);

  // B-NEW-04 (SCRUM-1151): group-first block creation for both single-date and range
  const blockDates = async ({ startDate, endDate, reason = undefined }) => {
    try {
      await apiRequest(`/api/v1/availability/${guideId}/block`, {
        method: 'POST',
        body: JSON.stringify({ startDate, endDate, ...(reason !== undefined ? { reason } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      });
      refresh();
    } catch (err) {
      // apiRequest throws on non-2xx; re-throw with status so BlockDateModal can handle 409
      if (err && err.status === 409) {
        const conflict = new Error(err.error || err.message || 'Cannot block dates with confirmed bookings');
        conflict.status = 409;
        throw conflict;
      }
      throw err;
    }
  };

  // B-NEW-06: Edit block API call (SCRUM-1153)
  const editBlock = useCallback(async ({ startDate, endDate, label }) => {
    if (!editingBlockGroup) { return; }
    try {
      await apiRequest(`/api/v1/availability/${guideId}/block/${editingBlockGroup.id}`, {
        method: 'PUT',
        body: JSON.stringify({ startDate, endDate, label }),
        headers: { 'Content-Type': 'application/json' },
      });
      refresh();
    } catch (err) {
      if (err && err.status === 409) {
        const conflict = new Error(err.error || err.message || 'New range contains a booked date');
        conflict.status = 409;
        throw conflict;
      }
      throw err;
    }
  }, [editingBlockGroup, guideId, refresh]);

  // B-NEW-06: Edit success handler — shows undo toast (SCRUM-1153)
  const handleEditSuccess = useCallback(() => {
    showUndoToast({
      message: 'Block updated',
      onCommit: async () => {
        // Edit already applied — nothing to do
      },
      onUndo: async () => {
        // Re-apply old range would require previous state — just refresh
        refresh();
      },
      onCommitError: () => {
        setErrorToast("Couldn't finalize edit");
      },
      onUndoError: () => {
        setErrorToast("Couldn't undo edit — please re-edit the block manually");
      },
    });
    setEditingBlockGroup(null);
  }, [showUndoToast, refresh]);

  // B-03: dateInfo for selected date
  // eslint-disable-next-line security/detect-object-injection -- selectedDate is a YYYY-MM-DD string from calendar state, not user input
  const selectedDateInfo = selectedDate
    ? (Object.prototype.hasOwnProperty.call(calendarData, selectedDate) ? calendarData[selectedDate] : null)
    : null;

  // B-NEW-06: Remove block handler (SCRUM-1153, SCRUM-1557: undo reinstates block)
  // Must be after selectedDateInfo declaration
  const handleRemoveBlock = useCallback(async (blockGroupId) => {
    // Capture block metadata before DELETE so the undo callback can reinstate it
    const blockSnapshot = selectedDateInfo ? {
      startDate: selectedDateInfo.blockGroupStart,
      endDate: selectedDateInfo.blockGroupEnd,
      reason: selectedDateInfo.blockGroupLabel || undefined,
    } : null;

    try {
      await apiRequest(`/api/v1/availability/${guideId}/block/${blockGroupId}`, {
        method: 'DELETE',
      });
      refresh();
      showUndoToast({
        message: 'Block removed',
        onCommit: async () => {
          // Delete already committed — nothing to do
        },
        onUndo: async () => {
          if (blockSnapshot?.startDate && blockSnapshot?.endDate) {
            await apiRequest(`/api/v1/availability/${guideId}/block`, {
              method: 'POST',
              body: JSON.stringify({
                startDate: blockSnapshot.startDate,
                endDate: blockSnapshot.endDate,
                ...(blockSnapshot.reason ? { reason: blockSnapshot.reason } : {}),
              }),
              headers: { 'Content-Type': 'application/json' },
            });
          }
          refresh();
        },
        onCommitError: () => {
          setErrorToast("Couldn't finalize removal");
        },
        onUndoError: () => {
          setErrorToast("Couldn't undo removal — please re-create the block manually");
        },
      });
    } catch (err) {
      setErrorToast('Failed to remove block. Please try again.');
    }
  }, [guideId, refresh, showUndoToast, selectedDateInfo]);

  // B-NEW-06: Edit block handler (SCRUM-1153)
  // Must be after selectedDateInfo declaration
  const handleEditBlock = useCallback((blockGroupId) => {
    const entry = selectedDateInfo;
    if (entry && entry.blockGroupId === blockGroupId) {
      setEditingBlockGroup({
        id: blockGroupId,
        start_date: entry.blockGroupStart,
        end_date: entry.blockGroupEnd,
        label: entry.blockGroupLabel || '',
      });
      setIsBlockModalOpen(true);
    }
  }, [selectedDateInfo]);

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Month navigation
  const navigateMonth = (direction) => {
    const [year, month] = currentMonth.split('-').map(Number);
    const date = new Date(year, month - 1 + direction, 1);
    setCurrentMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  };

  // Format month for display
  const displayMonth = useMemo(() => {
    const [year, month] = currentMonth.split('-').map(Number);
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="availability"
      onNavigate={handleNavigate}
      user={{
        name: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Guide',
        email: user?.email || '',
        avatar: null
      }}
    >
      <div className="flex flex-col gap-6 max-w-6xl mx-auto">
        {/* Page Header with Month Navigation */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Availability</h1>
            <p className="text-sm text-gray-600 mt-1">
              Click any date to view or change it
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigateMonth(-1)}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-700 hover:bg-gray-100 rounded-lg"
              style={{ fontSize: '13px', borderRadius: '8px' }}
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-gray-900 text-center" style={{ minWidth: '110px' }}>
              {displayMonth}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="inline-flex items-center justify-center w-8 h-8 text-gray-700 hover:bg-gray-100 rounded-lg"
              style={{ fontSize: '13px', borderRadius: '8px' }}
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-1.5 ml-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Two-column layout: Calendar (left) + DateDetailPanel (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Calendar Grid (Chunk B — AC-04 through AC-10) */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-8">
              {loading && Object.keys(calendarData).length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                  <span className="ml-2 text-gray-500">Loading availability...</span>
                </div>
              ) : (
                <>
                  <GuideAvailabilityCalendar
                    calendarData={calendarData}
                    currentMonth={currentMonth}
                    loading={loading}
                    onDateSelect={setSelectedDate}
                    selectedDate={selectedDate}
                  />
                  <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-green-400 inline-block" /> Available
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-red-400 inline-block" /> Manually Blocked
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-blue-400 inline-block" /> Repeating Block
                    </span>
                    {/* SCRUM-1511: distinguish confirmed booking dates from manual blocks */}
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded-full bg-brand-primary inline-block" /> Confirmed Booking
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: Date detail panel (A-03 — SCRUM-1138, A-05 — SCRUM-1140) */}
          <div className="lg:col-span-1">
            <DateDetailPanel
              selectedDate={selectedDate}
              guideId={guideId}
              dateInfo={selectedDateInfo}
              toggleDate={toggleDate}
              onBlockDate={() => { setEditingBlockGroup(null); setIsBlockModalOpen(true); }}
              onReopenSuccess={handleReopenSuccess}
              onManageRepeatingBlocks={() => setShowRepeatingBlocks(true)}
              onEditBlock={handleEditBlock}
              onRemoveBlock={handleRemoveBlock}
            />
            <CalendarSyncSettings />
            {/* B-09: Repeating blocks link (SCRUM-1154) */}
            <div className="mt-3">
              <button
                onClick={() => setShowRepeatingBlocks(true)}
                className="text-sm text-brand-primary hover:text-brand-primary-hover underline"
                data-testid="repeating-blocks-footer-link"
              >
                Repeating blocks
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* B-03 / B-NEW-04 / B-NEW-06: BlockDateModal */}
      <BlockDateModal
        isOpen={isBlockModalOpen}
        selectedDate={selectedDate}
        onClose={() => { setIsBlockModalOpen(false); setEditingBlockGroup(null); }}
        onBlockSuccess={() => {
          if (editingBlockGroup) {
            handleEditSuccess();
          } else if (selectedDate) {
            handleBlockSuccess(selectedDate);
          }
        }}
        blockDates={blockDates}
        toggleDate={toggleDate}
        createBlock={createBlock}
        editMode={!!editingBlockGroup}
        blockGroup={editingBlockGroup}
        onEditBlock={editBlock}
      />

      {/* B-09: RepeatingBlocksList overlay (SCRUM-1154) */}
      {showRepeatingBlocks && (
        <RepeatingBlocksList
          guideId={guideId}
          onClose={() => setShowRepeatingBlocks(false)}
        />
      )}

      {/* A-08: Undo toast */}
      {toastState && (
        <UndoToast
          message={toastState.message}
          variant="success"
          onUndo={undoToast}
          onDismiss={dismissToast}
        />
      )}

      {/* A-08: Error toast */}
      {errorToast && (
        <UndoToast
          message={errorToast}
          variant="error"
          onDismiss={() => setErrorToast(null)}
        />
      )}
    </DashboardLayout>
  );
}
